import React, { useState, useEffect, useRef } from 'react';
import { API_URL, getImageUrl } from '../../utils/api';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import { LoadingSpinner } from '../ui/Icons';
import Confetti from 'react-confetti';
import ImageCarousel from '../common/ImageCarousel';
import { GraduationCap, Brain, Timer } from 'lucide-react';

import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';

const PflanzeInSchlauQuiz = ({ deck, onFinish, onReview, trackInteraction, userPlantData, onAbort, theme }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userInput, setUserInput] = useState('');
    const [isAnswered, setIsAnswered] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [results, setResults] = useState([]);
    const [timeLeft, setTimeLeft] = useState(30);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const inputRef = useRef(null);

    const currentCard = deck[currentIndex];
    const userCardData = userPlantData?.[currentCard?.id] || {};
    const currentStatus = userCardData.learning_status || 'NOT_LEARNED';

    useEffect(() => {
        if (inputRef.current && !isAnswered && timeLeft > 0) {
            inputRef.current.focus();
        }
    }, [currentIndex, isAnswered, timeLeft]);

    useEffect(() => {
        if (currentCard) {
            setUserInput('');
            setIsAnswered(false);
            setIsCorrect(false);
            setTimeLeft(30);
        }
    }, [currentIndex, currentCard]);

    useEffect(() => {
        if (isAnswered || !currentCard || timeLeft <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleTimeout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [currentIndex, currentCard, isAnswered, timeLeft]);

    const normalizeString = (str) => {
        return str.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue');
    };

    const checkAnswer = (input) => {
        if (!currentCard) return false;
        return normalizeString(input) === normalizeString(currentCard.botanical_name || '');
    };

    const handleTimeout = () => {
        if (isAnswered) return;
        setIsAnswered(true);
        setIsCorrect(false);
        setStreak(0);

        const result = { plant: currentCard, correct: false, userInput: '', timeout: true };
        const updatedResults = [...results, result];
        setResults(updatedResults);
        if (onReview) onReview(currentCard, false, null, 30);
        if (trackInteraction) {
            trackInteraction('FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                mode: 'pflanze_in_schlau',
                status: 'timeout',
                time_taken: 30
            });
        }
        setTimeout(handleNext, 2500);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isAnswered || !userInput.trim() || timeLeft <= 0) return;

        const timeTaken = 30 - timeLeft;
        const correct = checkAnswer(userInput);
        setIsCorrect(correct);
        setIsAnswered(true);

        const result = { plant: currentCard, correct, userInput: userInput.trim() };
        const updatedResults = [...results, result];
        setResults(updatedResults);

        if (correct) {
            setScore(p => p + 1);
            setStreak(p => {
                const newStreak = p + 1;
                if (newStreak > bestStreak) setBestStreak(newStreak);
                return newStreak;
            });
        } else {
            setStreak(0);
        }

        if (onReview) onReview(currentCard, correct, null, timeTaken);

        if (trackInteraction) {
            trackInteraction(correct ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                user_input: userInput,
                time_taken: timeTaken,
                mode: 'pflanze_in_schlau'
            });
        }
        setTimeout(() => {
            if (currentIndex < deck.length - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                onFinish(updatedResults);
            }
        }, correct ? 1500 : 3000);
    };

    const handleNext = () => {
        if (currentIndex < deck.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            onFinish(results);
        }
    };

    if (!currentCard) return <LoadingSpinner />;

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">
            {isCorrect && <Confetti numberOfPieces={100} recycle={false} gravity={0.3} style={{ zIndex: 100 }} />}

            {/* Premium Header */}
            <QuizHeader
                current={currentIndex + 1}
                total={deck.length}
                onClose={onAbort}
                status={currentStatus}
                theme={theme}
            />

            {/* Timer Bar */}
            <div className="absolute top-0 left-0 w-full h-1.5 z-[70] overflow-hidden">
                <div 
                    className={`h-full transition-all duration-1000 ease-linear ${timeLeft < 10 ? 'bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.8)]' : 'bg-plantlix-500 shadow-[0_0_15px_rgba(16,185,129,0.8)]'}`}
                    style={{ width: `${(timeLeft / 30) * 100}%` }}
                />
            </div>

            {/* Workspace */}
            <div className="flex-1 flex flex-col min-h-0 space-y-3 px-4 md:px-12 pb-4 md:pb-8">
                {/* Image Container */}
                <div className="flex-1 min-h-0 relative w-full aspect-[21/9] md:aspect-[21/9] bg-slate-900 rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] border border-white/10 group">
                    <ImageCarousel
                        images={currentCard.images}
                        alt={currentCard.german_name}
                        className="h-full w-full object-cover transition-transform duration-2000 group-hover:scale-110 opacity-90"
                        showThumbnails={false}
                    />

                    {/* Stats Overlays */}
                    <div className="absolute top-3 inset-x-3 flex justify-between z-40">
                         <div className="flex items-center gap-2 px-3 py-1.5 bg-plantlix-500/90 backdrop-blur-xl border border-plantlix-400/50 rounded-xl shadow-lg text-white">
                            <Brain className="w-4 h-4 fill-white/20" />
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-80 leading-none">Genie</span>
                                <span className="text-xs font-black tracking-tighter tabular-nums leading-none">{score}</span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 px-4 py-1.5 bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl">
                             <Timer className={`w-4 h-4 ${timeLeft < 10 ? 'text-rose-500 animate-pulse' : 'text-white'}`} />
                             <span className={`text-md font-black tracking-tighter tabular-nums ${timeLeft < 10 ? 'text-rose-500' : 'text-white'}`}>{timeLeft}s</span>
                        </div>
                    </div>

                    {/* Question Overlay */}
                    <div className="absolute bottom-3 inset-x-3 p-3 md:p-6 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] shadow-2xl text-center z-30">
                        <span className="text-[8px] uppercase tracking-[0.5em] font-black text-plantlix-400 mb-1 block drop-shadow-md">Botanik-Genie</span>
                        <h3 className="text-xl md:text-3xl font-black text-white leading-tight tracking-tight italic drop-shadow-2xl">
                            {currentCard.german_name}
                        </h3>
                    </div>
                </div>

                {/* Input Area */}
                <div className="flex-shrink-0 flex flex-col space-y-3">
                    <form onSubmit={handleSubmit} className="relative w-full group">
                        <input
                            ref={inputRef}
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            disabled={isAnswered || timeLeft <= 0}
                            placeholder="Tippe den botanischen Namen..."
                            className={`
                                w-full ${theme === 'dark' ? 'bg-white/5 text-white placeholder-white/10' : 'bg-white/80 text-slate-900 placeholder-slate-300'} backdrop-blur-2xl border-2 rounded-[1.8rem] px-6 py-4 md:py-6 text-center text-xl md:text-3xl font-black outline-none transition-all shadow-2xl
                                ${isAnswered ? (isCorrect ? 'border-plantlix-500/50 bg-plantlix-500/10' : 'border-rose-500/50 bg-rose-500/10') : 'border-white/10 focus:border-plantlix-400 focus:bg-white/10'}
                            `}
                            autoComplete="off"
                            autoCorrect="off"
                        />
                        {!isAnswered && userInput.trim() && (
                            <button type="submit" className="absolute right-3 top-3 bottom-3 aspect-square bg-plantlix-500 hover:bg-plantlix-400 text-white rounded-2xl flex items-center justify-center shadow-2xl transition-all active:scale-90 overflow-hidden">
                                <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity" />
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={5} d="M5 13l4 4L19 7" /></svg>
                            </button>
                        )}
                    </form>

                    {/* Compact Feedback Notification */}
                    {isAnswered && (
                        <div className={`
                            w-full p-4 rounded-[1.8rem] border backdrop-blur-2xl flex items-center gap-4 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-500
                            ${isCorrect ? 'bg-plantlix-500/10 border-plantlix-500/20' : 'bg-rose-500/10 border-rose-500/20'}
                        `}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg shrink-0 shadow-lg ${isCorrect ? 'bg-plantlix-500 text-white shadow-plantlix-500/30' : 'bg-rose-500 text-white shadow-rose-500/30'}`}>
                                {isCorrect ? '✓' : '✗'}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className={`text-[8px] uppercase font-black tracking-[0.2em] mb-0.5 ${isCorrect ? 'text-plantlix-500' : 'text-rose-500'}`}>
                                    {isCorrect ? 'Perfekt!' : 'Lösung:'}
                                </p>
                                <p className={`text-lg md:text-2xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} truncate italic tracking-tight`}>
                                    {currentCard.botanical_name}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </QuizLayout>
    );
};

export default PflanzeInSchlauQuiz;
